import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiUrl } from "../../config/api";
import ConfirmDialog from "../globalcontrols/confirmdialog/ConfirmDialog";
import AnimatedTabPanel from "./AnimatedTabPanel";
import "./tableeAdmin.css";
import RentalReturnPanel from "../commerce/RentalReturnPanel";

const ORDER_STATUSES = [
  { value: "nuevo", label: "Nuevo" },
  { value: "en_revision", label: "En revision" },
  { value: "confirmado", label: "Confirmado" },
  { value: "preparacion", label: "Preparacion" },
  { value: "despachado", label: "Despachado" },
  { value: "entregado", label: "Entregado" },
  { value: "cerrado", label: "Cerrado" },
  { value: "cancelado", label: "Cancelado" },
];

function getDefaultTableePublicBaseUrl() {
  if (typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname)) {
    return "http://127.0.0.1:5180";
  }
  if (process.env.REACT_APP_TABLEE_PUBLIC_BASE_URL) return process.env.REACT_APP_TABLEE_PUBLIC_BASE_URL;
  return "https://tablee.cl";
}

const TABLEE_PUBLIC_BASE_URL = getDefaultTableePublicBaseUrl();

function getStatusLabel(value) {
  return ORDER_STATUSES.find((status) => status.value === value)?.label || value || "Estado";
}

function isOrderPaid(order) {
  return order?.paymentStatus === "approved" || Boolean(order?.paidAt);
}

function getRefundableAmount(order) {
  const backendValue = Number(order?.refundableAmount);
  if (Number.isFinite(backendValue)) return Math.max(0, backendValue);
  return Math.max(0, Number(order?.total || 0) - Number(order?.refundedAmount || 0));
}

// Card payments are refunded through the provider; transfers are paid back by the business and
// recorded here with the bank reference.
function isManualRefundOrder(order) {
  return !["mercadopago", "webpay"].includes(getPaymentMethodValue(order));
}

// Refunds whose result the provider never confirmed; staff record what actually happened.
function getRefundsToReconcile(order) {
  const openRefunds = Array.isArray(order?.refundSummary?.openRefunds) ? order.refundSummary.openRefunds : [];
  const staleAfter = Date.now() - 5 * 60 * 1000;
  return openRefunds.filter((refund) => refund.status === "unknown"
    || new Date(refund.updatedAt || refund.createdAt || 0).getTime() < staleAfter);
}

function todayIsoDate() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function emptyRefundDraft() {
  return { type: "full", amount: "", reason: "", manualReference: "", manualRefundedOn: todayIsoDate(), loading: false, message: "" };
}

function canRefundOrder(order) {
  return Boolean(order?.id)
    && (!order?.rental || (["nuevo", "en_revision", "confirmado", "preparacion"].includes(order.status) && ["confirmed", "awaiting_payment", "unavailable_requires_refund"].includes(order.rental.status)))
    && isOrderPaid(order)
    && !["refunded", "cancelled", "rejected"].includes(String(order?.paymentStatus || "").toLowerCase())
    && getRefundableAmount(order) > 0;
}

function canDeleteOrder(order) {
  return Boolean(order?.id) && !isOrderPaid(order);
}

function getPaymentLabel(order) {
  if (order?.paymentStatus === "refunded") return "Devuelto";
  if (order?.paymentStatus === "partially_refunded") return "Devolucion parcial";
  if (isOrderPaid(order)) return "Pagado";
  if (order?.paymentStatus === "rejected") return "Rechazado";
  if (order?.paymentStatus === "cancelled") return "Cancelado";
  if (order?.paymentStatus === "in_process") return "En proceso";
  if (order?.paymentStatus === "pending_manual") return "Pago pendiente";
  if (order?.paymentStatus === "pending") return "Pago pendiente";
  return order?.paymentStatus || "Pago pendiente";
}

function getPaymentTone(order) {
  if (["refunded", "partially_refunded"].includes(order?.paymentStatus)) return "review";
  if (isOrderPaid(order)) return "paid";
  if (["rejected", "cancelled"].includes(order?.paymentStatus)) return "failed";
  if (order?.paymentStatus === "in_process") return "review";
  return "pending";
}

function getPaymentMethodLabel(order) {
  const method = String(order?.paymentMethod || "").toLowerCase();
  if (method === "transfer") return "Transferencia";
  if (["mercadopago", "mercado_pago", "online", "mp"].includes(method)) return "Mercado Pago";
  if (["webpay", "transbank", "webpay_plus"].includes(method)) return "Webpay";
  return order?.paymentMethod || "-";
}

function getPaymentMethodValue(order) {
  const method = String(order?.paymentMethod || "").toLowerCase();
  if (method === "transfer") return "transfer";
  if (["mercadopago", "mercado_pago", "online", "mp"].includes(method)) return "mercadopago";
  if (["webpay", "transbank", "webpay_plus"].includes(method)) return "webpay";
  return method || "unknown";
}

function formatSelectedOptions(selectedOptions) {
  if (!selectedOptions || typeof selectedOptions !== "object" || Array.isArray(selectedOptions)) return "";
  return Object.entries(selectedOptions)
    .filter(([name, value]) => String(name || "").trim() && String(value || "").trim())
    .map(([name, value]) => `${name}: ${value}`)
    .join(" · ");
}

function getMarketingAttributionRows(order) {
  const attribution = order?.marketingAttribution;
  if (!attribution || typeof attribution !== "object" || Array.isArray(attribution)) return [];
  return [
    ["Fuente", attribution.utm_source],
    ["Medio", attribution.utm_medium],
    ["Campaña", attribution.utm_campaign || attribution.utm_id],
    ["Anuncio", attribution.utm_content],
    ["Termino", attribution.utm_term],
    ["Google click", attribution.gclid || attribution.gbraid || attribution.wbraid],
    ["Meta click", attribution.fbclid],
  ].filter(([, value]) => String(value || "").trim());
}

function getPaymentStatusValue(order) {
  if (["refunded", "partially_refunded"].includes(order?.paymentStatus)) return order.paymentStatus;
  if (isOrderPaid(order)) return "approved";
  return String(order?.paymentStatus || "pending").toLowerCase();
}

function getPaymentStatusFilterLabel(value) {
  if (value === "refunded") return "Devuelto";
  if (value === "partially_refunded") return "Devolucion parcial";
  if (value === "approved") return "Pagado";
  if (value === "pending_manual") return "Pago pendiente";
  if (value === "pending") return "Pago pendiente";
  if (value === "in_process") return "En proceso";
  if (value === "rejected") return "Rechazado";
  if (value === "cancelled") return "Cancelado";
  return value || "Pago pendiente";
}

function getStatusIndex(value) {
  return ORDER_STATUSES.findIndex((status) => status.value === value);
}

function isBackwardStatusChange(currentStatus, nextStatus) {
  const currentIndex = getStatusIndex(currentStatus);
  const nextIndex = getStatusIndex(nextStatus);
  return currentIndex >= 0 && nextIndex >= 0 && nextIndex < currentIndex;
}

function getForwardStatusOptions(currentStatus) {
  const currentIndex = getStatusIndex(currentStatus);
  if (currentIndex < 0) return ORDER_STATUSES;
  return ORDER_STATUSES.filter((_, index) => index >= currentIndex);
}

function getAllowedOrderStatuses(order, options) {
  if (!order.rental) return options;
  return options.filter((status) => {
    if (status.value === order.status) return true;
    if (status.value === "cancelado") return !isOrderPaid(order) && ["nuevo", "en_revision", "confirmado", "preparacion"].includes(order.status);
    if (["preparacion", "despachado", "entregado"].includes(status.value)) return order.rental.status === "confirmed";
    return true;
  });
}

function getSessionHeaders() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  });
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("es-CL", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function splitOrderNotes(notes) {
  const lines = String(notes || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const deliveryLine = lines.find((line) => /^despacho:/i.test(line));
  const deliveryDateLine = lines.find((line) => /^fecha de despacho:/i.test(line));
  return {
    deliverySelection: deliveryLine ? deliveryLine.replace(/^despacho:\s*/i, "") : "",
    deliveryDate: deliveryDateLine ? deliveryDateLine.replace(/^fecha de despacho:\s*/i, "").replace(/\.$/, "") : "",
    customerNotes: lines.filter((line) => !/^despacho:/i.test(line) && !/^fecha de despacho:/i.test(line)).join("\n"),
  };
}

function normalizeSearchText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function addressIncludesCommune(address, commune) {
  const normalizedAddress = normalizeSearchText(address);
  const normalizedCommune = normalizeSearchText(commune);
  return Boolean(normalizedAddress && normalizedCommune && normalizedAddress.includes(normalizedCommune));
}

function getOrderCoordinates(order) {
  const latitude = Number(order?.deliveryLatitude);
  const longitude = Number(order?.deliveryLongitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
}

function formatDateOnly(value) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("es-CL", { dateStyle: "medium" }).format(new Date(`${value}T00:00:00`));
}

function getCustomerDisplayName(order) {
  const name = String(order?.customerName || "").trim();
  const id = String(order?.customerId || "").trim();
  if (!name) return "Cliente sin nombre";
  if (id && name === id) return "Cliente sin nombre";
  if (/^(cliente\s*)?#?\d+$/i.test(name)) return "Cliente sin nombre";
  return name;
}

function getFilePreviewType(fileName = "", mimeType = "") {
  const normalizedType = String(mimeType || "").toLowerCase();
  const normalizedName = String(fileName || "").toLowerCase();
  if (normalizedType.startsWith("image/") || /\.(png|jpe?g|webp|gif|bmp)$/i.test(normalizedName)) return "image";
  if (normalizedType === "application/pdf" || /\.pdf$/i.test(normalizedName)) return "pdf";
  return "file";
}

function getTableePublicUrl(path, publicBaseUrl = TABLEE_PUBLIC_BASE_URL) {
  const baseUrl = publicBaseUrl.replace(/\/+$/, "");
  return `${baseUrl}/#${path.startsWith("/") ? path : `/${path}`}`;
}

function encodeOrderConfirmationPayload(order) {
  const payload = {
    publicId: order?.publicId || "",
    trackingNumber: order?.trackingNumber || "",
    status: order?.status || "nuevo",
    createdAt: order?.createdAt || "",
    customerName: order?.customerName || "",
    customerEmail: order?.customerEmail || "",
    customerPhone: order?.customerPhone || "",
    deliveryAddress: order?.deliveryFormattedAddress || order?.deliveryAddress || "",
    deliveryCommune: order?.deliveryCommune || "",
    deliveryMethod: "",
    preferredDeliveryDate: order?.preferredDeliveryDate || "",
    paymentMethod: getPaymentMethodValue(order) === "transfer" ? "transfer" : "online",
    paymentStatus: order?.paymentStatus || "",
    subtotal: Number(order?.subtotal || 0),
    shipping: Number(order?.shipping || 0),
    total: Number(order?.total || 0),
    notes: order?.notes || "",
    items: Array.isArray(order?.items) ? order.items.map((item) => ({
      id: item.productId || item.id || item.sku || item.productName,
      productId: item.productId || item.id || "",
      sku: item.sku || "",
      productName: item.productName || "Producto",
      name: item.productName || "Producto",
      selectedOptions: item.selectedOptions || {},
      quantity: Number(item.quantity || 0),
      unitPrice: Number(item.unitPrice || 0),
      subtotal: Number(item.subtotal || 0),
    })) : [],
  };
  return btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
}

function getOrderConfirmationUrl(order, publicBaseUrl) {
  const publicUrl = (path) => getTableePublicUrl(path, publicBaseUrl);
  if (typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname)) {
    try {
      return publicUrl(`/checkout-confirmacion?order=${encodeURIComponent(encodeOrderConfirmationPayload(order))}`);
    } catch {
      return publicUrl("/checkout");
    }
  }

  const publicId = String(order?.publicId || "").trim();
  if (publicId) {
    if (order?.paymentPreferenceId) {
      return publicUrl(`/checkout/${encodeURIComponent(publicId)}?preference_id=${encodeURIComponent(order.paymentPreferenceId)}`);
    }
    if (order?.paymentId) {
      return publicUrl(`/checkout/${encodeURIComponent(publicId)}?payment_id=${encodeURIComponent(order.paymentId)}`);
    }
  }
  if (order?.trackingNumber) {
    return publicUrl(`/seguimiento/${encodeURIComponent(order.trackingNumber)}`);
  }
  return publicUrl("/seguimiento");
}

function getOrderActionsMenuPosition(triggerElement, actionCount) {
  const rect = triggerElement.getBoundingClientRect();
  const width = 220;
  const viewportPadding = 12;
  const gap = 6;
  const estimatedMenuHeight = Math.min(360, Math.max(52, (actionCount * 41) + 12));
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const spaceBelow = viewportHeight - rect.bottom - viewportPadding;
  const spaceAbove = rect.top - viewportPadding;
  const direction = spaceBelow >= estimatedMenuHeight || spaceBelow >= spaceAbove ? "down" : "up";
  const maxHeight = Math.max(120, (direction === "down" ? spaceBelow : spaceAbove) - gap);
  const top = direction === "down"
    ? Math.min(rect.bottom + gap, viewportHeight - viewportPadding)
    : Math.max(viewportPadding, rect.top - gap - Math.min(estimatedMenuHeight, maxHeight));
  const preferredLeft = rect.right - width;
  const left = Math.min(
    Math.max(viewportPadding, preferredLeft),
    Math.max(viewportPadding, viewportWidth - width - viewportPadding)
  );

  return {
    top,
    left,
    width,
    direction,
    maxHeight,
  };
}

function OrderStatusDropdown({ order, dropdownKey, options = ORDER_STATUSES, isOpen, menuPosition, menuMode = "fixed", onOpen, onClose, onChange, label }) {
  const isInlineMenu = menuMode === "inline";

  return (
    <div className={`tablee-admin__status-dropdown ${isInlineMenu ? "tablee-admin__status-dropdown--inline" : ""}`} data-label={label}>
      <div className="tablee-admin__status-control">
        <button
          type="button"
          className={`tablee-admin__status-trigger tablee-admin__status-trigger--${order.status}`}
          onClick={(event) => {
            event.stopPropagation();
            if (isOpen) {
              onClose();
            } else {
              const rect = event.currentTarget.getBoundingClientRect();
              onOpen(dropdownKey || `order-${order.id}`, {
                top: rect.bottom + 6,
                left: rect.left,
                width: rect.width,
              });
            }
          }}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span>{getStatusLabel(order.status)}</span>
          <span className="material-symbols-outlined" aria-hidden="true">expand_more</span>
        </button>
      </div>
      {isOpen ? (
        <div
          className={`tablee-admin__status-menu ${isInlineMenu ? "tablee-admin__status-menu--inline" : ""}`}
          role="listbox"
          style={isInlineMenu ? {
            minWidth: menuPosition.width,
          } : {
            top: menuPosition.top,
            left: menuPosition.left,
            minWidth: menuPosition.width,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {options.map((status) => (
            <button
              type="button"
              key={status.value}
              className={status.value === order.status ? "is-selected" : ""}
              role="option"
              aria-selected={status.value === order.status}
              onClick={() => {
                onClose();
                if (status.value !== order.status) {
                  onChange(order, status.value);
                }
              }}
            >
              {status.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FilterDropdown({ label, value, options, isOpen, align = "left", onOpen, onClose, onChange }) {
  const selectedOption = options.find((option) => option.value === value) || options[0];

  return (
    <div className="tablee-admin__filter-field">
      <span>{label}</span>
      <div className="tablee-admin__filter-dropdown">
        <button
          type="button"
          className="tablee-admin__filter-trigger"
          onClick={(event) => {
            event.stopPropagation();
            isOpen ? onClose() : onOpen();
          }}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="material-symbols-outlined" aria-hidden="true">filter_list</span>
          <strong>{selectedOption?.label || "Todos"}</strong>
          <span className="material-symbols-outlined" aria-hidden="true">expand_more</span>
        </button>
        {isOpen ? (
          <div
            className={`tablee-admin__filter-menu tablee-admin__filter-menu--${align}`}
            role="listbox"
            onClick={(event) => event.stopPropagation()}
            onWheel={(event) => event.stopPropagation()}
            onTouchMove={(event) => event.stopPropagation()}
          >
            {options.map((option) => (
              <button
                type="button"
                key={option.value}
                className={option.value === value ? "is-selected" : ""}
                role="option"
                aria-selected={option.value === value}
                onClick={() => {
                  onChange(option.value);
                  onClose();
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function OrderActionsMenu({ order, isOpen, menuPosition, onOpen, onClose, actions }) {
  return (
    <div className="tablee-admin__order-actions-menu" data-label="Acciones">
      <button
        type="button"
        className="tablee-admin__order-actions-trigger"
        onClick={(event) => {
          event.stopPropagation();
          if (isOpen) {
            onClose();
            return;
          }
          onOpen(`actions-${order.id}`, getOrderActionsMenuPosition(event.currentTarget, actions.length));
        }}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={`Abrir acciones de ${order.publicId}`}
      >
        <span className="material-symbols-outlined" aria-hidden="true">more_horiz</span>
      </button>
      {isOpen ? (
        <div
          className={`tablee-admin__order-actions-dropdown tablee-admin__order-actions-dropdown--${menuPosition.direction || "down"}`}
          role="menu"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
            width: menuPosition.width,
            maxHeight: menuPosition.maxHeight,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {actions.map((action) => (
            <button
              type="button"
              key={action.key}
              className={action.tone === "danger" ? "is-danger" : ""}
              role="menuitem"
              onClick={() => {
                onClose();
                action.onClick();
              }}
            >
              <span className="material-symbols-outlined" aria-hidden="true">{action.icon}</span>
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TableeEcommerceOrders({ apiPrefix = "/tablee", storeName = "Tablee", storeLogo = "/images/tablee_logo.png", storeUrl = "www.tablee.cl", publicBaseUrl, labelsPath = "/app/tablee-etiquetas" } = {}) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [message, setMessage] = useState("");
  const [activeModal, setActiveModal] = useState(null);
  const [openStatusMenu, setOpenStatusMenu] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [openFilterMenu, setOpenFilterMenu] = useState("");
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [orderFilters, setOrderFilters] = useState({
    status: "all",
    paymentMethod: "all",
    paymentStatus: "all",
  });
  const [orderDetailTab, setOrderDetailTab] = useState("data");
  const [documentType, setDocumentType] = useState("boleta");
  const [uploadState, setUploadState] = useState({ loading: false, message: "" });
  const [uploadPreview, setUploadPreview] = useState(null);
  const [refundDraft, setRefundDraft] = useState(emptyRefundDraft);
  const [reconcileDraft, setReconcileDraft] = useState({ references: {}, loading: false, message: "" });

  function clearUploadPreview() {
    setUploadPreview((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }
      return null;
    });
  }

  async function loadStoredDocumentPreview(order) {
    if (!order?.invoiceReceipt?.previewUrl) return;
    const filePreviewType = getFilePreviewType(order.invoiceReceipt.fileName, order.invoiceReceipt.mimeType);
    const isPdf = filePreviewType === "pdf";
    const fallbackThumbnailUrl = isPdf && order.id ? `${apiPrefix}/orders/${order.id}/invoice-receipt-thumbnail` : "";
    const sourceUrl = order.invoiceReceipt.thumbnailUrl || fallbackThumbnailUrl || (isPdf ? "" : order.invoiceReceipt.previewUrl);
    if (!sourceUrl) return;
    const isThumbnail = isPdf || Boolean(order.invoiceReceipt.thumbnailUrl);
    if (isThumbnail) {
      setUploadPreview((current) => {
        if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
        return {
          previewUrl: "",
          fileName: order.invoiceReceipt.fileName,
          mimeType: order.invoiceReceipt.mimeType,
          previewType: "pending",
          isStaged: false,
        };
      });
    }
    try {
      const response = await fetch(apiUrl(sourceUrl), {
        headers: getSessionHeaders(),
      });
      if (!response.ok) {
        setUploadPreview((current) => current?.previewType === "pending"
          ? { ...current, previewType: "file" }
          : current);
        return;
      }
      const blob = await response.blob();
      const previewUrl = URL.createObjectURL(blob);
      setUploadPreview((current) => {
        if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
        return {
          previewUrl,
          fileName: order.invoiceReceipt.fileName,
          mimeType: order.invoiceReceipt.mimeType || blob.type,
          previewType: isThumbnail ? "image" : getFilePreviewType(order.invoiceReceipt.fileName, order.invoiceReceipt.mimeType || blob.type),
          isStaged: false,
        };
      });
    } catch (error) {
      setUploadPreview((current) => current?.previewType === "pending"
        ? { ...current, previewType: "file" }
        : current);
    }
  }

  async function refreshOrderDocumentPreview(order) {
    if (!order?.id) return;
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/orders`), {
        headers: getSessionHeaders(),
      });
      const data = await response.json();
      if (!data.success) return;
      const freshOrder = (data.orders || []).find((entry) => entry.id === order.id);
      if (!freshOrder) return;
      setOrders(data.orders || []);
      setActiveModal((current) => current?.type === "upload" && current.order?.id === order.id
        ? { ...current, order: freshOrder }
        : current);
      setDocumentType(freshOrder.invoiceReceipt?.type || "boleta");
      if (freshOrder.invoiceReceipt) {
        await loadStoredDocumentPreview(freshOrder);
      }
    } catch (error) {
      // The existing order snapshot is still usable if the refresh fails.
    }
  }

  const loadOrders = useCallback(async () => {
    const response = await fetch(apiUrl(`${apiPrefix}/orders`), {
      headers: getSessionHeaders(),
    });
    const data = await response.json();
    if (data.success) {
      setOrders(data.orders || []);
      return data.orders || [];
    } else {
      setMessage(data.message || "No se pudieron cargar las ordenes.");
    }
  }, [apiPrefix]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const paymentMethodOptions = useMemo(() => {
    const entries = new Map([
      ["transfer", "Transferencia"],
      ["mercadopago", "Mercado Pago"],
      ["webpay", "Webpay"],
    ]);
    orders.forEach((order) => {
      const value = getPaymentMethodValue(order);
      if (!entries.has(value)) entries.set(value, getPaymentMethodLabel(order));
    });
    return Array.from(entries, ([value, label]) => ({ value, label }));
  }, [orders]);

  const paymentStatusOptions = useMemo(() => {
    const values = new Set(["approved", "partially_refunded", "refunded", "pending_manual", "pending", "in_process", "rejected", "cancelled"]);
    orders.forEach((order) => values.add(getPaymentStatusValue(order)));
    return Array.from(values).filter(Boolean).map((value) => ({
      value,
      label: getPaymentStatusFilterLabel(value),
    }));
  }, [orders]);

  const filteredOrders = useMemo(() => orders.filter((order) => {
    if (orderFilters.status !== "all" && order.status !== orderFilters.status) return false;
    if (orderFilters.paymentMethod !== "all" && getPaymentMethodValue(order) !== orderFilters.paymentMethod) return false;
    if (orderFilters.paymentStatus !== "all" && getPaymentStatusValue(order) !== orderFilters.paymentStatus) return false;
    return true;
  }), [orders, orderFilters]);

  const hasActiveFilters = Object.values(orderFilters).some((value) => value !== "all");

  function updateOrderFilter(name, value) {
    setOrderFilters((current) => ({ ...current, [name]: value }));
  }

  useEffect(() => {
    if (!openStatusMenu && !openActionMenu && !openFilterMenu) return undefined;
    const closeMenu = () => {
      setOpenStatusMenu(null);
      setOpenActionMenu(null);
      setOpenFilterMenu("");
    };
    document.addEventListener("click", closeMenu);
    window.addEventListener("resize", closeMenu);
    if (openStatusMenu || openActionMenu) {
      window.addEventListener("scroll", closeMenu, true);
    }
    return () => {
      document.removeEventListener("click", closeMenu);
      window.removeEventListener("resize", closeMenu);
      if (openStatusMenu || openActionMenu) {
        window.removeEventListener("scroll", closeMenu, true);
      }
    };
  }, [openStatusMenu, openActionMenu, openFilterMenu]);

  async function updateStatus(order, status) {
    setMessage("");
    const response = await fetch(apiUrl(`${apiPrefix}/orders/${order.id}/status`), {
      method: "PUT",
      headers: getSessionHeaders(),
      body: JSON.stringify({ status }),
    });
    const data = await response.json();
    if (!data.success) {
      setMessage(data.message || "No se pudo actualizar la orden.");
      return null;
    }
    setOrders((current) => current.map((entry) => (entry.id === order.id ? { ...entry, ...data.order } : entry)));
    setActiveModal((current) => current?.order?.id === order.id ? { ...current, order: { ...current.order, ...data.order } } : current);
    return data.order;
  }

  async function deleteOrder(order) {
    setMessage("");
    const response = await fetch(apiUrl(`${apiPrefix}/orders/${order.id}`), {
      method: "DELETE",
      headers: getSessionHeaders(),
    });
    const data = await response.json();
    if (!data.success) {
      setMessage(data.message || "No se pudo eliminar la orden.");
      return;
    }
    setOrders((current) => current.filter((entry) => entry.id !== order.id));
    setActiveModal((current) => current?.order?.id === order.id ? null : current);
    setMessage(`Orden ${order.publicId} eliminada.`);
  }

  function requestDeleteOrder(order) {
    setConfirmDialog({
      title: "Eliminar orden",
      message: `Eliminar la orden ${order.publicId}? Solo se puede eliminar si el pago no esta confirmado.`,
      confirmLabel: "Eliminar",
      tone: "danger",
      onConfirm: async () => {
        setConfirmDialog(null);
        await deleteOrder(order);
      },
    });
  }

  function openRefundModal(order) {
    const refundableAmount = getRefundableAmount(order);
    setRefundDraft({ ...emptyRefundDraft(), amount: String(Math.round(refundableAmount)) });
    openModal("refund", order);
  }

  function updateRefundDraft(name, value) {
    if (name === "type" && value === "partial" && activeModal?.order?.rental) return;
    setRefundDraft((current) => ({ ...current, [name]: value, message: "" }));
  }

  async function submitRefund(order) {
    const refundableAmount = getRefundableAmount(order);
    const amount = refundDraft.type === "full" ? refundableAmount : Math.round(Number(refundDraft.amount || 0));
    if (!Number.isFinite(amount) || amount <= 0) {
      setRefundDraft((current) => ({ ...current, message: "Ingresa un monto mayor a cero." }));
      return;
    }
    if (amount > refundableAmount) {
      setRefundDraft((current) => ({ ...current, message: "El monto supera el saldo disponible para devolver." }));
      return;
    }
    const manual = isManualRefundOrder(order);
    if (manual && !refundDraft.manualReference.trim()) {
      setRefundDraft((current) => ({ ...current, message: "Registra la referencia de la transferencia de devolución." }));
      return;
    }

    setRefundDraft((current) => ({ ...current, loading: true, message: "" }));
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/orders/${order.id}/refund`), {
        method: "POST",
        headers: getSessionHeaders(),
        body: JSON.stringify({
          type: refundDraft.type,
          amount,
          reason: refundDraft.reason,
          ...(manual ? { manualReference: refundDraft.manualReference.trim(), manualRefundedOn: refundDraft.manualRefundedOn } : {}),
        }),
      });
      const data = await response.json();
      if (!data.success) {
        setRefundDraft((current) => ({ ...current, loading: false, message: data.message || "No se pudo procesar la devolucion." }));
        // An uncertain refund now holds its amount; show it so it can be reconciled.
        if (data.refundStatus) loadOrders();
        return;
      }
      setOrders((current) => current.map((entry) => (entry.id === order.id ? data.order : entry)));
      setActiveModal(null);
      setRefundDraft(emptyRefundDraft());
      setMessage(refundDraft.type === "full"
        ? `Orden ${order.publicId} cancelada y devuelta.`
        : `Devolucion parcial creada para ${order.publicId}.`);
    } catch (error) {
      setRefundDraft((current) => ({ ...current, loading: false, message: "No se pudo procesar la devolucion." }));
    }
  }

  function requestRefundConfirmation(order) {
    const refundableAmount = getRefundableAmount(order);
    const amount = refundDraft.type === "full" ? refundableAmount : Math.round(Number(refundDraft.amount || 0));
    if (!Number.isFinite(amount) || amount <= 0) {
      setRefundDraft((current) => ({ ...current, message: "Ingresa un monto mayor a cero." }));
      return;
    }
    if (amount > refundableAmount) {
      setRefundDraft((current) => ({ ...current, message: "El monto supera el saldo disponible para devolver." }));
      return;
    }

    if (isManualRefundOrder(order) && !refundDraft.manualReference.trim()) {
      setRefundDraft((current) => ({ ...current, message: "Registra la referencia de la transferencia de devolución." }));
      return;
    }

    const isTotalRefund = amount === refundableAmount;
    const destination = isManualRefundOrder(order)
      ? `Se registrara la devolucion de ${formatMoney(amount)} por transferencia (referencia ${refundDraft.manualReference.trim()})`
      : null;
    setConfirmDialog({
      title: refundDraft.type === "full"
        ? "Cancelar orden y devolver"
        : isTotalRefund
          ? "Confirmar devolucion total"
          : "Crear devolucion parcial",
      message: destination
        ? `${destination}${refundDraft.type === "full" || isTotalRefund ? ` y se cancelara la orden ${order.publicId}` : ` para la orden ${order.publicId}`}. Esta accion no se puede deshacer desde ${storeName}.`
        : refundDraft.type === "full"
        ? `Se cancelara la orden ${order.publicId} y se devolvera ${formatMoney(amount)} al medio de pago original. Esta accion no se puede deshacer desde ${storeName}.`
        : isTotalRefund
          ? `El monto seleccionado completa una devolucion total de la orden ${order.publicId}. Se devolvera ${formatMoney(amount)} al medio de pago original y la orden sera cancelada. Esta accion no se puede deshacer desde ${storeName}.`
        : `Se devolvera ${formatMoney(amount)} al medio de pago original de la orden ${order.publicId}. La orden seguira activa. Esta accion no se puede deshacer desde ${storeName}.`,
      confirmLabel: refundDraft.type === "full" || isTotalRefund ? "Confirmar devolucion total" : "Confirmar devolucion",
      tone: "danger",
      onConfirm: async () => {
        setConfirmDialog(null);
        await submitRefund(order);
      },
    });
  }

  function openReconcileModal(order) {
    setReconcileDraft({ references: {}, loading: false, message: "" });
    openModal("reconcile", order);
  }

  async function reconcileRefund(order, refund, outcome) {
    const reference = String(reconcileDraft.references[refund.id] || "").trim();
    if (!reference) {
      setReconcileDraft((current) => ({ ...current, message: "Registra la referencia o comprobante que revisaste en el proveedor." }));
      return;
    }
    setReconcileDraft((current) => ({ ...current, loading: true, message: "" }));
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/orders/${order.id}/refunds/${refund.id}/reconcile`), {
        method: "POST",
        headers: getSessionHeaders(),
        body: JSON.stringify({ outcome, reference }),
      });
      const data = await response.json();
      if (!data.success) {
        setReconcileDraft((current) => ({ ...current, loading: false, message: data.message || "No se pudo conciliar la devolucion." }));
        return;
      }
      setOrders((current) => current.map((entry) => (entry.id === order.id ? data.order : entry)));
      setReconcileDraft({ references: {}, loading: false, message: "" });
      if (getRefundsToReconcile(data.order).length) {
        setActiveModal({ type: "reconcile", order: data.order });
      } else {
        setActiveModal(null);
      }
      setMessage(outcome === "succeeded"
        ? `Devolucion de ${order.publicId} registrada como realizada.`
        : `Devolucion de ${order.publicId} registrada como no realizada; el monto vuelve a estar disponible.`);
    } catch (error) {
      setReconcileDraft((current) => ({ ...current, loading: false, message: "No se pudo conciliar la devolucion." }));
    }
  }

  function requestModalStatusChange(order, status) {
    if (status === order.status) return;
    if (!isBackwardStatusChange(order.status, status)) {
      updateStatus(order, status);
      return;
    }
    setConfirmDialog({
      title: "Retroceder estado del pedido",
      message: `La orden ${order.publicId} pasara de ${getStatusLabel(order.status)} a ${getStatusLabel(status)}. Confirma este cambio solo si necesitas corregir el flujo del pedido.`,
      confirmLabel: "Cambiar estado",
      tone: "danger",
      onConfirm: async () => {
        setConfirmDialog(null);
        await updateStatus(order, status);
      },
    });
  }

  function openModal(type, order) {
    setUploadState({ loading: false, message: "" });
    clearUploadPreview();
    setOpenStatusMenu(null);
    setOpenActionMenu(null);
    if (type === "order") {
      setOrderDetailTab("data");
    }
    setActiveModal({ type, order });
    if (type === "upload") {
      setDocumentType(order.invoiceReceipt?.type || "boleta");
      loadStoredDocumentPreview(order);
      refreshOrderDocumentPreview(order);
    }
  }

  function closeModal() {
    setActiveModal(null);
    setRefundDraft(emptyRefundDraft());
    setReconcileDraft({ references: {}, loading: false, message: "" });
    clearUploadPreview();
  }

  async function handleDocumentSelection(order, event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadState({ loading: false, message: "" });
    try {
      const dataBase64 = await readFileAsDataUrl(file);
      const filePreviewType = getFilePreviewType(file.name, file.type);
      const isPdf = filePreviewType === "pdf";
      setUploadPreview((current) => {
        if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
        return {
          previewUrl: filePreviewType === "image" ? URL.createObjectURL(file) : "",
          dataUrl: dataBase64,
          fileName: file.name,
          mimeType: file.type,
          size: file.size,
          previewType: isPdf ? "pending" : filePreviewType,
          isStaged: true,
        };
      });
      if (isPdf) {
        await loadStagedDocumentThumbnail({
          dataUrl: dataBase64,
          fileName: file.name,
          mimeType: file.type,
          size: file.size,
        });
      }
    } catch (error) {
      setUploadState({ loading: false, message: "No se pudo leer el documento." });
    } finally {
      event.target.value = "";
    }
  }

  async function loadStagedDocumentThumbnail(stagedFile) {
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/orders/invoice-receipt-thumbnail-preview`), {
        method: "POST",
        headers: getSessionHeaders(),
        body: JSON.stringify({
          fileName: stagedFile.fileName,
          mimeType: stagedFile.mimeType,
          size: stagedFile.size,
          dataBase64: stagedFile.dataUrl,
        }),
      });
      if (!response.ok) {
        setUploadPreview((current) => current?.isStaged ? { ...current, previewType: "file" } : current);
        return;
      }
      const blob = await response.blob();
      const previewUrl = URL.createObjectURL(blob);
      setUploadPreview((current) => {
        if (!current?.isStaged) {
          URL.revokeObjectURL(previewUrl);
          return current;
        }
        if (current.previewUrl) URL.revokeObjectURL(current.previewUrl);
        return {
          ...current,
          previewUrl,
          previewType: "image",
        };
      });
    } catch (error) {
      setUploadPreview((current) => current?.isStaged ? { ...current, previewType: "file" } : current);
    }
  }

  async function saveDocument(order) {
    if (!uploadPreview?.isStaged || !uploadPreview.dataUrl) return;
    setUploadState({ loading: true, message: "" });
    if (getFilePreviewType(uploadPreview.fileName, uploadPreview.mimeType) === "pdf" && uploadPreview.previewType !== "image") {
      setUploadPreview((current) => current ? { ...current, previewType: "pending" } : current);
    }
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/orders/${order.id}/invoice-receipt`), {
        method: "PUT",
        headers: getSessionHeaders(),
        body: JSON.stringify({
          documentType,
          fileName: uploadPreview.fileName,
          mimeType: uploadPreview.mimeType,
          size: uploadPreview.size,
          dataBase64: uploadPreview.dataUrl,
        }),
      });
      const data = await response.json();
      if (!data.success) {
        if (getFilePreviewType(uploadPreview.fileName, uploadPreview.mimeType) === "pdf") {
          setUploadPreview((current) => current ? { ...current, previewType: "file" } : current);
        }
        setUploadState({ loading: false, message: data.message || "No se pudo guardar el documento." });
        return;
      }
      setOrders((current) => current.map((entry) => (entry.id === order.id ? data.order : entry)));
      setActiveModal((current) => current ? { ...current, order: data.order } : current);
      if (data.order?.invoiceReceipt?.thumbnailUrl) {
        if (uploadPreview.previewType === "image" && uploadPreview.previewUrl) {
          setUploadPreview((current) => current ? {
            ...current,
            fileName: data.order.invoiceReceipt.fileName,
            mimeType: data.order.invoiceReceipt.mimeType,
            isStaged: false,
          } : current);
        } else {
          clearUploadPreview();
          setUploadPreview({
            previewUrl: "",
            fileName: data.order.invoiceReceipt.fileName,
            mimeType: data.order.invoiceReceipt.mimeType,
            previewType: "pending",
            isStaged: false,
          });
          await loadStoredDocumentPreview(data.order);
        }
      } else if (getFilePreviewType(uploadPreview.fileName, uploadPreview.mimeType) === "pdf") {
        setUploadPreview({
          previewUrl: "",
          fileName: uploadPreview.fileName,
          mimeType: uploadPreview.mimeType,
          previewType: "file",
          isStaged: false,
        });
      }
      setUploadState({ loading: false, message: "" });
    } catch (error) {
      if (getFilePreviewType(uploadPreview.fileName, uploadPreview.mimeType) === "pdf") {
        setUploadPreview((current) => current ? { ...current, previewType: "file" } : current);
      }
      setUploadState({ loading: false, message: "No se pudo guardar el documento." });
    }
  }

  async function deleteDocument(order) {
    if (!order?.invoiceReceipt && !uploadPreview) return;
    if (uploadPreview?.isStaged) {
      clearUploadPreview();
      setUploadState({ loading: false, message: "" });
      if (order?.invoiceReceipt) {
        await loadStoredDocumentPreview(order);
      }
      return;
    }
    if (!order?.invoiceReceipt) {
      clearUploadPreview();
      setUploadState({ loading: false, message: "" });
      return;
    }

    setUploadState({ loading: true, message: "Eliminando documento..." });
    try {
      const response = await fetch(apiUrl(`${apiPrefix}/orders/${order.id}/invoice-receipt`), {
        method: "DELETE",
        headers: getSessionHeaders(),
      });
      const data = await response.json();
      if (!data.success) {
        setUploadState({ loading: false, message: data.message || "No se pudo eliminar el documento." });
        return;
      }
      clearUploadPreview();
      setOrders((current) => current.map((entry) => (entry.id === order.id ? data.order : entry)));
      setActiveModal((current) => current ? { ...current, order: data.order } : current);
      setUploadState({ loading: false, message: "Documento eliminado." });
    } catch (error) {
      setUploadState({ loading: false, message: "No se pudo eliminar el documento." });
    }
  }

  function requestDeleteDocument(order) {
    const isStaged = Boolean(uploadPreview?.isStaged);
    setConfirmDialog({
      title: isStaged ? "Quitar documento" : "Eliminar documento",
      message: isStaged
        ? "Quitar el documento seleccionado antes de guardarlo?"
        : `Eliminar el documento guardado de la orden ${order?.publicId || ""}?`,
      confirmLabel: isStaged ? "Quitar" : "Eliminar",
      tone: "danger",
      onConfirm: async () => {
        setConfirmDialog(null);
        await deleteDocument(order);
      },
    });
  }

  return (
    <section className="tablee-admin tablee-admin__orders tablee-admin__orders--ecommerce">
      <header className="tablee-admin__header">
        <img src={storeLogo} alt={storeName} />
        <div>
          <span>{storeName}</span>
          <h1>Ordenes ecommerce</h1>
          <p>Revisa los pedidos recibidos desde {storeUrl}.</p>
        </div>
      </header>

      <div className="tablee-admin__panel">
        <div className="tablee-admin__filter-bar" aria-label="Filtros de ordenes">
          <FilterDropdown
            label="Estado"
            value={orderFilters.status}
            options={[{ value: "all", label: "Todos" }, ...ORDER_STATUSES]}
            isOpen={openFilterMenu === "status"}
            onOpen={() => setOpenFilterMenu("status")}
            onClose={() => setOpenFilterMenu("")}
            onChange={(value) => updateOrderFilter("status", value)}
          />
          <FilterDropdown
            label="Medio de pago"
            value={orderFilters.paymentMethod}
            options={[{ value: "all", label: "Todos" }, ...paymentMethodOptions]}
            isOpen={openFilterMenu === "paymentMethod"}
            onOpen={() => setOpenFilterMenu("paymentMethod")}
            onClose={() => setOpenFilterMenu("")}
            onChange={(value) => updateOrderFilter("paymentMethod", value)}
          />
          <FilterDropdown
            label="Estado de pago"
            value={orderFilters.paymentStatus}
            options={[{ value: "all", label: "Todos" }, ...paymentStatusOptions]}
            isOpen={openFilterMenu === "paymentStatus"}
            align="right"
            onOpen={() => setOpenFilterMenu("paymentStatus")}
            onClose={() => setOpenFilterMenu("")}
            onChange={(value) => updateOrderFilter("paymentStatus", value)}
          />
          {hasActiveFilters ? (
            <button type="button" className="tablee-admin__filter-reset" onClick={() => setOrderFilters({ status: "all", paymentMethod: "all", paymentStatus: "all" })}>
              Limpiar
            </button>
          ) : null}
        </div>
        <div className="tablee-admin__table">
          <div className="tablee-admin__table-head" aria-hidden="true">
            <span>Orden</span>
            <span>Cliente</span>
            <span>Mail</span>
            <span>Fecha</span>
            <span>Items</span>
            <span>Total</span>
            <span>Pago</span>
            <span>Medio</span>
            <span>Estado</span>
            <span>Acciones</span>
            <span aria-label="Documento" />
          </div>
          <div className="tablee-admin__table-body">
            {filteredOrders.length ? filteredOrders.map((order) => {
              const rowActions = [
                { key: "order", label: "Ver orden", icon: "visibility", onClick: () => openModal("order", order) },
                { key: "confirmation", label: "Ver confirmacion", icon: "receipt_long", onClick: () => window.open(getOrderConfirmationUrl(order, publicBaseUrl), "_blank", "noopener,noreferrer") },
                { key: "items", label: "Ver items", icon: "list_alt", onClick: () => openModal("items", order) },
                { key: "client", label: "Ver cliente", icon: "person", onClick: () => openModal("client", order) },
                { key: "upload", label: "Factura / boleta", icon: "upload_file", onClick: () => openModal("upload", order) },
                { key: "labels", label: "Disenar etiquetas", icon: "sell", onClick: () => navigate(`${labelsPath}/${order.id}`) },
              ];
              if (canRefundOrder(order)) {
                rowActions.push({ key: "refund", label: order.rental ? "Cancelar arriendo y devolver todo" : "Devolver / cancelar", icon: "currency_exchange", tone: "danger", onClick: () => openRefundModal(order) });
              }
              if (getRefundsToReconcile(order).length) {
                rowActions.push({ key: "reconcile", label: "Conciliar devolucion", icon: "fact_check", tone: "danger", onClick: () => openReconcileModal(order) });
              }
              if (canDeleteOrder(order)) {
                rowActions.push({ key: "delete", label: "Eliminar orden", icon: "delete", tone: "danger", onClick: () => requestDeleteOrder(order) });
              }
              return (
                <article className="tablee-admin__table-row" key={order.id}>
                  <div className="tablee-admin__main-cell" data-label="Orden">
                    <strong>{order.publicId}</strong>
                    <small>{order.trackingNumber || "Sin seguimiento"}</small>
                    {order.rental ? <small>Arriendo · {formatDateOnly(order.rental.startDate)} → {formatDateOnly(order.rental.endDate)}</small> : null}
                    <small className="tablee-admin__desktop-date">{formatDate(order.createdAt)}</small>
                  </div>
                  <div className="tablee-admin__main-cell" data-label="Cliente">
                    <strong>{getCustomerDisplayName(order)}</strong>
                    <small>{order.customerPhone || "-"}</small>
                  </div>
                  <a className="tablee-admin__email-cell" href={`mailto:${order.customerEmail}`} data-label="Mail">
                    {order.customerEmail || "-"}
                  </a>
                  <span data-label="Fecha">{formatDate(order.createdAt)}</span>
                  <strong data-label="Items">{order.itemCount}</strong>
                  <strong data-label="Total">
                    {formatMoney(order.total)}
                    {Number(order.discount || 0) > 0 ? <small>{order.promotionCode} · -{formatMoney(order.discount)}</small> : null}
                  </strong>
                  <span className="tablee-admin__payment-cell" data-label="Pago">
                    <span className={`tablee-admin__payment-badge is-${getPaymentTone(order)}`}>
                      {getPaymentLabel(order)}
                    </span>
                  </span>
                  <span className="tablee-admin__payment-method" data-label="Medio">{getPaymentMethodLabel(order)}</span>
                  <OrderStatusDropdown
                    order={order}
                    dropdownKey={`row-${order.id}`}
                    options={getAllowedOrderStatuses(order, getForwardStatusOptions(order.status))}
                    isOpen={openStatusMenu?.key === `row-${order.id}`}
                    menuPosition={openStatusMenu?.position || { top: 0, left: 0, width: 140 }}
                    onOpen={(key, position) => {
                      setOpenActionMenu(null);
                      setOpenStatusMenu({ key, position });
                    }}
                    onClose={() => setOpenStatusMenu(null)}
                    onChange={updateStatus}
                    label="Estado"
                  />
                  <div className="tablee-admin__row-actions" data-label="Acciones">
                    <OrderActionsMenu
                      order={order}
                      actions={rowActions}
                      isOpen={openActionMenu?.key === `actions-${order.id}`}
                      menuPosition={openActionMenu?.position || { top: 0, left: 0, width: 220 }}
                      onOpen={(key, position) => {
                        setOpenStatusMenu(null);
                        setOpenActionMenu({ key, position });
                      }}
                      onClose={() => setOpenActionMenu(null)}
                    />
                    <button type="button" className="tablee-admin__icon-button" onClick={() => openModal("order", order)} title="Ver orden" aria-label={`Ver orden ${order.publicId}`}>
                      <span className="material-symbols-outlined">visibility</span>
                    </button>
                    <button type="button" className="tablee-admin__icon-button" onClick={() => openModal("items", order)} title="Ver items" aria-label={`Ver items de ${order.publicId}`}>
                      <span className="material-symbols-outlined">list_alt</span>
                    </button>
                    <button type="button" className="tablee-admin__icon-button" onClick={() => openModal("client", order)} title="Ver cliente" aria-label={`Ver datos del cliente de ${order.publicId}`}>
                      <span className="material-symbols-outlined">person</span>
                    </button>
                    <button type="button" className="tablee-admin__icon-button tablee-admin__icon-button--upload" onClick={() => openModal("upload", order)} title="Subir factura/boleta" aria-label={`Subir factura o boleta para ${order.publicId}`}>
                      <span className="material-symbols-outlined">upload_file</span>
                    </button>
                    <button type="button" className="tablee-admin__icon-button" onClick={() => navigate(`${labelsPath}/${order.id}`)} title="Diseñar etiquetas" aria-label={`Diseñar etiquetas para ${order.publicId}`}>
                      <span className="material-symbols-outlined">sell</span>
                    </button>
                    {canDeleteOrder(order) ? (
                      <button type="button" className="tablee-admin__order-delete-button" onClick={() => requestDeleteOrder(order)} title="Eliminar orden" aria-label={`Eliminar orden ${order.publicId}`}>
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    ) : null}
                  </div>
                  <span className="tablee-admin__document-cell" aria-label="Documento">
                    {order.invoiceReceipt ? (
                      <span className="tablee-admin__document-dot" title={`Documento cargado: ${order.invoiceReceipt.fileName}`} aria-label="Documento cargado" />
                    ) : null}
                  </span>
                </article>
              );
            }) : (
              <p className="tablee-admin__empty">{message || (orders.length ? "No hay ordenes para los filtros seleccionados." : "No hay ordenes registradas.")}</p>
            )}
          </div>
        </div>
      </div>

      {activeModal ? (
        <div className="tablee-admin__modal-backdrop" role="presentation" onMouseDown={closeModal}>
          <div className={`tablee-admin__modal tablee-admin__modal--${activeModal.type}`} role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <div className="tablee-admin__modal-head">
              <div>
                <span>Orden {activeModal.order.publicId}</span>
                <h2>
                  {activeModal.type === "items"
                    ? "Items del pedido"
                    : activeModal.type === "upload"
                      ? "Factura / Boleta"
                      : activeModal.type === "refund"
                        ? "Devolucion / cancelacion"
                        : activeModal.type === "reconcile"
                          ? "Conciliar devolucion"
                        : activeModal.type === "client"
                          ? "Datos del cliente"
                          : "Detalle de la orden"}
                </h2>
              </div>
              <button type="button" className="tablee-admin__modal-close" onClick={closeModal} aria-label="Cerrar">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            {activeModal.type === "order" ? (
              <div className="tablee-admin__modal-body">
                {(() => {
                  const noteDetails = splitOrderNotes(activeModal.order.notes);
                  const coordinates = getOrderCoordinates(activeModal.order);
                  const shouldShowCommune = activeModal.order.deliveryCommune
                    && !addressIncludesCommune(activeModal.order.deliveryAddress, activeModal.order.deliveryCommune);
                  const mapsUrl = coordinates
                    ? `https://www.google.com/maps?q=${coordinates.latitude},${coordinates.longitude}`
                    : "";
                  const mapsEmbedUrl = coordinates
                    ? `https://www.google.com/maps?q=${coordinates.latitude},${coordinates.longitude}&z=17&output=embed`
                    : "";
                  const activeResponsiveDetailTab = orderDetailTab === "data" ? "general" : orderDetailTab;
                  const generalSection = (
                    <div className="tablee-admin__order-summary">
                      <div>
                        <span>Estado</span>
                        <OrderStatusDropdown
                          order={activeModal.order}
                          dropdownKey={`modal-${activeModal.order.id}`}
                          options={getAllowedOrderStatuses(activeModal.order, ORDER_STATUSES)}
                          isOpen={openStatusMenu?.key === `modal-${activeModal.order.id}`}
                          menuMode="inline"
                          menuPosition={openStatusMenu?.position || { top: 0, left: 0, width: 140 }}
                          onOpen={(key, position) => setOpenStatusMenu({ key, position })}
                          onClose={() => setOpenStatusMenu(null)}
                          onChange={requestModalStatusChange}
                        />
                      </div>
                      <div>
                        <span>Fecha</span>
                        <strong>{formatDate(activeModal.order.createdAt)}</strong>
                      </div>
                      <div>
                        <span>Seguimiento</span>
                        <strong>{activeModal.order.trackingNumber || "-"}</strong>
                      </div>
                      <div>
                        <span>Pago</span>
                        <strong>{getPaymentLabel(activeModal.order)}</strong>
                      </div>
                      <div className="tablee-admin__order-total">
                        <span>Total</span>
                        <strong>{formatMoney(activeModal.order.total)}</strong>
                      </div>
                    </div>
                  );
                  const clientDeliverySections = (
                    <>
                      <section className="tablee-admin__detail-section">
                        <div className="tablee-admin__section-title">
                          <span className="material-symbols-outlined">person</span>
                          <h3>Cliente</h3>
                        </div>
                        <div className="tablee-admin__detail-grid tablee-admin__detail-grid--customer">
                          <div>
                            <span>Nombre</span>
                            <strong>{getCustomerDisplayName(activeModal.order)}</strong>
                          </div>
                          <div>
                            <span>Email</span>
                            <strong>{activeModal.order.customerEmail || "-"}</strong>
                          </div>
                          <div>
                            <span>Telefono</span>
                            <strong>{activeModal.order.customerPhone || "-"}</strong>
                          </div>
                          {activeModal.order.taxDetails ? (
                            <>
                              <div>
                                <span>Documento</span>
                                <strong>{activeModal.order.taxDetails.documentType === "factura" ? "Factura" : "Boleta"}</strong>
                              </div>
                              <div>
                                <span>RUT</span>
                                <strong>{activeModal.order.taxDetails.companyRut || activeModal.order.taxDetails.rut || "-"}</strong>
                              </div>
                              {activeModal.order.taxDetails.clientType === "empresa" ? (
                                <>
                                  <div>
                                    <span>Razón social</span>
                                    <strong>{activeModal.order.taxDetails.companyName || "-"}</strong>
                                  </div>
                                  <div>
                                    <span>Giro</span>
                                    <strong>{activeModal.order.taxDetails.companyBusinessActivity || "-"}</strong>
                                  </div>
                                  <div>
                                    <span>Dirección comercial</span>
                                    <strong>{activeModal.order.taxDetails.companyAddress || "-"}</strong>
                                  </div>
                                </>
                              ) : null}
                            </>
                          ) : null}
                        </div>
                      </section>

                      <section className="tablee-admin__detail-section">
                        <div className="tablee-admin__section-title">
                          <span className="material-symbols-outlined">local_shipping</span>
                          <h3>Despacho</h3>
                        </div>
                        <div className="tablee-admin__delivery-card">
                          <div className="tablee-admin__delivery-method">
                            <span>Metodo</span>
                            <strong>{noteDetails.deliverySelection || "-"}</strong>
                          </div>
                          <div className="tablee-admin__delivery-method">
                            <span>Fecha de despacho</span>
                            <strong>{formatDateOnly(activeModal.order.preferredDeliveryDate || noteDetails.deliveryDate)}</strong>
                          </div>
                          <div className="tablee-admin__delivery-address">
                            <span>Direccion</span>
                            <strong>{activeModal.order.deliveryAddress || "-"}</strong>
                          </div>
                          {shouldShowCommune ? (
                            <div className="tablee-admin__delivery-commune">
                              <span>Comuna</span>
                              <strong>{activeModal.order.deliveryCommune}</strong>
                            </div>
                          ) : null}
                        </div>
                      </section>
                    </>
                  );
                  const paymentSection = (
                    <section className="tablee-admin__detail-section">
                      <div className="tablee-admin__section-title">
                        <span className="material-symbols-outlined">payments</span>
                        <h3>Resumen de pago</h3>
                      </div>
                      <div className="tablee-admin__amount-list">
                        <div>
                          <span>Subtotal</span>
                          <strong>{formatMoney(activeModal.order.subtotal)}</strong>
                        </div>
                        {Number(activeModal.order.discount || 0) > 0 ? (
                          <div>
                            <span>Descuento {activeModal.order.promotionCode ? `(${activeModal.order.promotionCode})` : ""}</span>
                            <strong>-{formatMoney(activeModal.order.discount)}</strong>
                          </div>
                        ) : null}
                        <div>
                          <span>Despacho</span>
                          <strong>{formatMoney(activeModal.order.shipping)}</strong>
                        </div>
                        <div>
                          <span>Total</span>
                          <strong>{formatMoney(activeModal.order.total)}</strong>
                        </div>
                        <div>
                          <span>Metodo</span>
                          <strong>{activeModal.order.paymentMethod || activeModal.order.paymentProvider || "-"}</strong>
                        </div>
                        <div>
                          <span>ID pago</span>
                          <strong>{activeModal.order.paymentId || "-"}</strong>
                        </div>
                        <div>
                          <span>Fecha pago</span>
                          <strong>{formatDate(activeModal.order.paidAt)}</strong>
                        </div>
                      </div>
                    </section>
                  );
                  return (
                    <div className="tablee-admin__order-tabs">
                      <RentalReturnPanel key={activeModal.order.id} order={activeModal.order} apiPrefix={apiPrefix} onUpdated={async (updatedOrder) => {
                        const freshOrders = await loadOrders();
                        const fresh = updatedOrder || freshOrders?.find((entry) => entry.id === activeModal.order.id);
                        if (fresh) setActiveModal((current) => current?.order?.id === fresh.id ? { ...current, order: fresh } : current);
                      }} />
                      <div className="tablee-admin__tab-slider tablee-admin__tab-slider--desktop" role="tablist" aria-label="Detalle de orden">
                        <span className="tablee-admin__tab-slider-thumb" aria-hidden="true" />
                        <button
                          type="button"
                          className={orderDetailTab === "data" ? "is-active" : ""}
                          onClick={() => setOrderDetailTab("data")}
                          role="tab"
                          aria-selected={orderDetailTab === "data"}
                        >
                          Datos
                        </button>
                        <button
                          type="button"
                          className={orderDetailTab === "notes" ? "is-active" : ""}
                          onClick={() => setOrderDetailTab("notes")}
                          role="tab"
                          aria-selected={orderDetailTab === "notes"}
                        >
                          Notas
                        </button>
                        <button
                          type="button"
                          className={orderDetailTab === "map" ? "is-active" : ""}
                          onClick={() => setOrderDetailTab("map")}
                          role="tab"
                          aria-selected={orderDetailTab === "map"}
                        >
                          Mapa
                        </button>
                      </div>
                      <div className="tablee-admin__tab-slider tablee-admin__tab-slider--phone" role="tablist" aria-label="Detalle de orden en telefono">
                        <span className="tablee-admin__tab-slider-thumb" aria-hidden="true" />
                        <button
                          type="button"
                          className={activeResponsiveDetailTab === "general" ? "is-active" : ""}
                          onClick={() => setOrderDetailTab("general")}
                          role="tab"
                          aria-selected={activeResponsiveDetailTab === "general"}
                        >
                          General
                        </button>
                        <button
                          type="button"
                          className={activeResponsiveDetailTab === "client" ? "is-active" : ""}
                          onClick={() => setOrderDetailTab("client")}
                          role="tab"
                          aria-selected={activeResponsiveDetailTab === "client"}
                        >
                          Cliente
                        </button>
                        <button
                          type="button"
                          className={activeResponsiveDetailTab === "payment" ? "is-active" : ""}
                          onClick={() => setOrderDetailTab("payment")}
                          role="tab"
                          aria-selected={activeResponsiveDetailTab === "payment"}
                        >
                          Pago
                        </button>
                        <button
                          type="button"
                          className={activeResponsiveDetailTab === "notes" ? "is-active" : ""}
                          onClick={() => setOrderDetailTab("notes")}
                          role="tab"
                          aria-selected={activeResponsiveDetailTab === "notes"}
                        >
                          Notas
                        </button>
                        <button
                          type="button"
                          className={activeResponsiveDetailTab === "map" ? "is-active" : ""}
                          onClick={() => setOrderDetailTab("map")}
                          role="tab"
                          aria-selected={activeResponsiveDetailTab === "map"}
                        >
                          Mapa
                        </button>
                      </div>

                      <AnimatedTabPanel tabKey={orderDetailTab}>
                        {orderDetailTab === "data" ? (
                          <>
                            <div className="tablee-admin__order-detail tablee-admin__order-detail--desktop-data">
                              {generalSection}
                              {clientDeliverySections}
                              {paymentSection}
                            </div>
                            <div className="tablee-admin__order-detail tablee-admin__order-detail--phone-data">
                              {generalSection}
                            </div>
                          </>
                        ) : orderDetailTab === "general" ? (
                          <div className="tablee-admin__order-detail">
                            {generalSection}
                          </div>
                        ) : orderDetailTab === "client" ? (
                          <div className="tablee-admin__order-detail">
                            {clientDeliverySections}
                          </div>
                        ) : orderDetailTab === "payment" ? (
                          <div className="tablee-admin__order-detail">
                            {paymentSection}
                          </div>
                        ) : orderDetailTab === "notes" ? (
                          <div className="tablee-admin__order-detail tablee-admin__order-detail--notes">
                            <section className="tablee-admin__detail-section">
                              <div className="tablee-admin__section-title">
                                <span className="material-symbols-outlined">notes</span>
                                <h3>Notas del pedido</h3>
                              </div>
                              <div className="tablee-admin__notes">
                                {noteDetails.customerNotes ? (
                                  <p>{noteDetails.customerNotes}</p>
                                ) : (
                                  <p>Esta orden no tiene notas adicionales.</p>
                                )}
                              </div>
                            </section>
                          </div>
                        ) : (
                          <div className="tablee-admin__map-panel">
                            {coordinates ? (
                              <>
                                <div className="tablee-admin__map-frame">
                                  <iframe
                                    title={`Ubicacion ${activeModal.order.publicId}`}
                                    src={mapsEmbedUrl}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                  />
                                </div>
                                <div className="tablee-admin__map-meta">
                                  <div>
                                    <span>Coordenadas</span>
                                    <strong>{coordinates.latitude.toFixed(6)}, {coordinates.longitude.toFixed(6)}</strong>
                                  </div>
                                  <a href={mapsUrl} target="_blank" rel="noreferrer">
                                    <span className="material-symbols-outlined">open_in_new</span>
                                    Abrir en Maps
                                  </a>
                                </div>
                              </>
                            ) : (
                              <div className="tablee-admin__map-empty">
                                <span className="material-symbols-outlined">location_off</span>
                                <strong>Esta orden no tiene coordenadas guardadas.</strong>
                                <p>Las coordenadas se guardan cuando el pedido viene desde el checkout con una ubicacion seleccionada en el mapa.</p>
                              </div>
                            )}
                          </div>
                        )}
                      </AnimatedTabPanel>
                    </div>
                  );
                })()}
              </div>
            ) : null}

            {activeModal.type === "refund" ? (
              <div className="tablee-admin__modal-body">
                {(() => {
                  const refundableAmount = getRefundableAmount(activeModal.order);
                  const selectedAmount = refundDraft.type === "full"
                    ? refundableAmount
                    : Math.round(Number(refundDraft.amount || 0));
                  const partialAmount = Math.round(Number(refundDraft.amount || 0));
                  return (
                    <div className="tablee-admin__refund-panel">
                      <div className="tablee-admin__amount-list tablee-admin__refund-summary">
                        <div>
                          <span>Total pagado</span>
                          <strong>{formatMoney(activeModal.order.total)}</strong>
                        </div>
                        <div>
                          <span>Ya devuelto</span>
                          <strong>{formatMoney(activeModal.order.refundedAmount)}</strong>
                        </div>
                        <div>
                          <span>Disponible</span>
                          <strong>{formatMoney(refundableAmount)}</strong>
                        </div>
                      </div>

                      {activeModal.order.rental ? <p className="commerce-rental__note">Cancelación antes del despacho: se devuelve el pago completo, incluida la garantía, y se liberan las unidades reservadas. Para productos ya entregados, registra la devolución e inspección desde el detalle del arriendo.</p> : null}
                      <div className="tablee-admin__refund-options" role="radiogroup" aria-label="Tipo de devolucion">
                        <label className={refundDraft.type === "full" ? "is-selected" : ""}>
                          <input
                            type="radio"
                            name="refundType"
                            value="full"
                            checked={refundDraft.type === "full"}
                            onChange={() => updateRefundDraft("type", "full")}
                          />
                          <span className="material-symbols-outlined" aria-hidden="true">cancel</span>
                          <span className="tablee-admin__refund-option-copy">
                            <strong>Cancelar orden y devolver todo</strong>
                            <small>{formatMoney(refundableAmount)}</small>
                          </span>
                        </label>
                        {!activeModal.order.rental ? (
                        <label className={refundDraft.type === "partial" ? "is-selected" : ""}>
                          <input
                            type="radio"
                            name="refundType"
                            value="partial"
                            checked={refundDraft.type === "partial"}
                            onChange={() => updateRefundDraft("type", "partial")}
                          />
                          <span className="material-symbols-outlined" aria-hidden="true">payments</span>
                          <span className="tablee-admin__refund-option-copy">
                            <strong>Devolucion parcial</strong>
                            <small>Elegir monto - {formatMoney(partialAmount)}</small>
                          </span>
                        </label>
                        ) : null}
                      </div>

                      {refundDraft.type === "partial" ? (
                        <label className="tablee-admin__refund-field">
                          <span>Monto a devolver</span>
                          <input
                            type="number"
                            min="1"
                            max={Math.round(refundableAmount)}
                            step="1"
                            value={refundDraft.amount}
                            onChange={(event) => updateRefundDraft("amount", event.target.value)}
                          />
                        </label>
                      ) : null}

                      {isManualRefundOrder(activeModal.order) ? (
                        <>
                          <p className="commerce-rental__note">Este pago fue por transferencia: primero transfiere el dinero de vuelta al cliente y luego registra aqui los datos de esa transferencia.</p>
                          <label className="tablee-admin__refund-field">
                            <span>Referencia de la transferencia</span>
                            <input
                              type="text"
                              value={refundDraft.manualReference}
                              onChange={(event) => updateRefundDraft("manualReference", event.target.value)}
                              placeholder="N° de operacion o comprobante"
                            />
                          </label>
                          <label className="tablee-admin__refund-field">
                            <span>Fecha de la transferencia</span>
                            <input
                              type="date"
                              value={refundDraft.manualRefundedOn}
                              onChange={(event) => updateRefundDraft("manualRefundedOn", event.target.value)}
                            />
                          </label>
                        </>
                      ) : null}

                      <label className="tablee-admin__refund-field">
                        <span>Motivo interno</span>
                        <textarea
                          rows={3}
                          value={refundDraft.reason}
                          onChange={(event) => updateRefundDraft("reason", event.target.value)}
                          placeholder="Opcional"
                        />
                      </label>

                      <div className="tablee-admin__refund-total">
                        <span>Monto que se devolvera</span>
                        <strong>{formatMoney(selectedAmount)}</strong>
                      </div>

                      {refundDraft.message ? <p className="tablee-admin__upload-message">{refundDraft.message}</p> : null}

                      <div className="tablee-admin__refund-actions">
                        <button type="button" className="tablee-admin__refund-secondary" onClick={closeModal} disabled={refundDraft.loading}>
                          Cancelar
                        </button>
                        <button type="button" className="tablee-admin__refund-confirm" onClick={() => requestRefundConfirmation(activeModal.order)} disabled={refundDraft.loading || selectedAmount <= 0 || selectedAmount > refundableAmount || (isManualRefundOrder(activeModal.order) && !refundDraft.manualReference.trim())}>
                          {refundDraft.loading ? "Procesando..." : isManualRefundOrder(activeModal.order) ? "Registrar devolucion" : refundDraft.type === "full" ? "Cancelar y devolver" : "Crear devolucion"}
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : null}

            {activeModal.type === "reconcile" ? (
              <div className="tablee-admin__modal-body">
                <div className="tablee-admin__refund-panel">
                  <p className="commerce-rental__note">El proveedor no confirmo el resultado de estas devoluciones. Revisa el panel de {activeModal.order.paymentMethod === "webpay" ? "Transbank" : "Mercado Pago"} o el banco y registra lo que paso. Esto no envia ni cobra dinero.</p>
                  {getRefundsToReconcile(activeModal.order).map((refund) => (
                    <div className="tablee-admin__refund-field" key={refund.id}>
                      <span>{formatMoney(refund.amount)} · solicitada {formatDate(refund.createdAt)}</span>
                      <input
                        type="text"
                        aria-label="Referencia revisada"
                        placeholder={refund.provider === "mercadopago" ? "ID de devolución de Mercado Pago" : "Referencia del proveedor o banco"}
                        value={reconcileDraft.references[refund.id] || ""}
                        onChange={(event) => {
                          const value = event.target.value;
                          setReconcileDraft((current) => ({ ...current, message: "", references: { ...current.references, [refund.id]: value } }));
                        }}
                      />
                      <div className="tablee-admin__refund-actions">
                        <button type="button" className="tablee-admin__refund-secondary" disabled={reconcileDraft.loading} onClick={() => reconcileRefund(activeModal.order, refund, "failed")}>
                          No se devolvio
                        </button>
                        <button type="button" className="tablee-admin__refund-confirm" disabled={reconcileDraft.loading} onClick={() => reconcileRefund(activeModal.order, refund, "succeeded")}>
                          Si se devolvio
                        </button>
                      </div>
                    </div>
                  ))}
                  {reconcileDraft.message ? <p className="tablee-admin__upload-message">{reconcileDraft.message}</p> : null}
                </div>
              </div>
            ) : null}

            {activeModal.type === "client" ? (
              <div className="tablee-admin__modal-body">
                {(() => {
                  const coordinates = getOrderCoordinates(activeModal.order);
                  const noteDetails = splitOrderNotes(activeModal.order.notes);
                  const marketingAttributionRows = getMarketingAttributionRows(activeModal.order);
                  const shouldShowCommune = activeModal.order.deliveryCommune
                    && !addressIncludesCommune(activeModal.order.deliveryAddress, activeModal.order.deliveryCommune);
                  return (
                    <div className="tablee-admin__client-detail">
                      <div className="tablee-admin__client-hero">
                        <span className="material-symbols-outlined">person</span>
                        <div>
                          <span>Cliente</span>
                          <strong>{getCustomerDisplayName(activeModal.order)}</strong>
                          <small>{activeModal.order.customerEmail || "Sin email registrado"}</small>
                        </div>
                      </div>

                      <section className="tablee-admin__detail-section">
                        <div className="tablee-admin__section-title">
                          <span className="material-symbols-outlined">contact_mail</span>
                          <h3>Contacto</h3>
                        </div>
                        <div className="tablee-admin__detail-grid tablee-admin__detail-grid--customer">
                          <div>
                            <span>Nombre</span>
                            <strong>{getCustomerDisplayName(activeModal.order)}</strong>
                          </div>
                          <div>
                            <span>Email</span>
                            <strong>{activeModal.order.customerEmail || "-"}</strong>
                          </div>
                          <div>
                            <span>Telefono</span>
                            <strong>{activeModal.order.customerPhone || "-"}</strong>
                          </div>
                        </div>
                      </section>

                      <section className="tablee-admin__detail-section">
                        <div className="tablee-admin__section-title">
                          <span className="material-symbols-outlined">location_on</span>
                          <h3>Direccion de esta orden</h3>
                        </div>
                        <div className="tablee-admin__delivery-card">
                          <div>
                            <span>Direccion</span>
                            <strong>{activeModal.order.deliveryAddress || "-"}</strong>
                          </div>
                          <div>
                            <span>Fecha de despacho</span>
                            <strong>{formatDateOnly(activeModal.order.preferredDeliveryDate || noteDetails.deliveryDate)}</strong>
                          </div>
                          {shouldShowCommune ? (
                            <div>
                              <span>Comuna</span>
                              <strong>{activeModal.order.deliveryCommune}</strong>
                            </div>
                          ) : null}
                          <div>
                            <span>Coordenadas</span>
                            <strong>{coordinates ? `${coordinates.latitude.toFixed(6)}, ${coordinates.longitude.toFixed(6)}` : "-"}</strong>
                          </div>
                        </div>
                      </section>

                      <section className="tablee-admin__detail-section">
                        <div className="tablee-admin__section-title">
                          <span className="material-symbols-outlined">receipt_long</span>
                          <h3>Contexto</h3>
                        </div>
                        <div className="tablee-admin__amount-list">
                          <div>
                            <span>Orden</span>
                            <strong>{activeModal.order.publicId}</strong>
                          </div>
                          <div>
                            <span>Origen</span>
                            <strong>{activeModal.order.source || "-"}</strong>
                          </div>
                          {marketingAttributionRows.map(([label, value]) => (
                            <div key={label}>
                              <span>{label}</span>
                              <strong>{value}</strong>
                            </div>
                          ))}
                          <div>
                            <span>Fecha</span>
                            <strong>{formatDate(activeModal.order.createdAt)}</strong>
                          </div>
                        </div>
                      </section>
                    </div>
                  );
                })()}
              </div>
            ) : null}

            {activeModal.type === "items" ? (
              <div className="tablee-admin__modal-body">
                <div className="tablee-admin__items-table">
                  <div className="tablee-admin__items-head">
                    <span>Producto</span>
                    <span>Cant.</span>
                    <span>Precio unit.</span>
                    <span>Total</span>
                  </div>
                  {(activeModal.order.items || []).map((item) => {
                    const selectedOptionsLabel = formatSelectedOptions(item.selectedOptions);
                    return (
                      <div className="tablee-admin__items-row" key={item.id || `${item.productId}-${item.productName}`}>
                        <div className="tablee-admin__main-cell" data-label="Producto">
                          <strong>{item.productName}</strong>
                          <small>{item.sku || item.presentation || "-"}</small>
                          {selectedOptionsLabel ? <small>{selectedOptionsLabel}</small> : null}
                        </div>
                        <strong data-label="Cant.">{item.quantity}</strong>
                        <strong data-label="Precio unit.">{formatMoney(item.unitPrice)}</strong>
                        <strong data-label="Total">{formatMoney(item.subtotal)}</strong>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {activeModal.type === "upload" ? (
              <div className="tablee-admin__modal-body">
                <div className="tablee-admin__document-panel">
                  <section className="tablee-admin__document-section">
                    <div className="tablee-admin__document-toolbar">
                      <label className={`tablee-admin__file-picker ${uploadState.loading ? "is-loading" : ""}`}>
                        <input type="file" accept=".pdf,image/*" onChange={(event) => handleDocumentSelection(activeModal.order, event)} disabled={uploadState.loading} />
                        <span className="material-symbols-outlined">upload_file</span>
                        <span className="tablee-admin__file-picker-copy">
                          <strong>{uploadState.loading ? "Subiendo..." : uploadPreview ? "Cambiar" : "Subir"}</strong>
                        </span>
                      </label>
                      <div className="tablee-admin__document-toggle" role="group" aria-label="Tipo de documento">
                        <button type="button" className={documentType === "boleta" ? "is-active" : ""} onClick={() => setDocumentType("boleta")}>Boleta</button>
                        <button type="button" className={documentType === "factura" ? "is-active" : ""} onClick={() => setDocumentType("factura")}>Factura</button>
                      </div>
                    </div>

                    <div className="tablee-admin__document-upload-area">
                      <div className={`tablee-admin__document-thumb-card ${uploadPreview ? "has-preview" : "is-empty"}`}>
                        {uploadPreview ? (
                          uploadPreview.previewType === "image" ? (
                            <img src={uploadPreview.previewUrl} alt="" />
                          ) : uploadPreview.previewType === "pending" || (uploadState.loading && uploadPreview.isStaged && getFilePreviewType(uploadPreview.fileName, uploadPreview.mimeType) === "pdf") ? (
                            <span className="tablee-admin__document-spinner" aria-label="Generando miniatura" />
                          ) : (
                            <span className="material-symbols-outlined">picture_as_pdf</span>
                          )
                        ) : (
                          <span className="material-symbols-outlined">draft</span>
                        )}
                        {uploadPreview ? (
                          <button
                            type="button"
                            className="tablee-admin__document-delete"
                            onClick={() => requestDeleteDocument(activeModal.order)}
                            disabled={uploadState.loading}
                            aria-label="Eliminar documento"
                            title="Eliminar documento"
                          >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M6.7 6.7 17.3 17.3M17.3 6.7 6.7 17.3" />
                            </svg>
                          </button>
                        ) : null}
                        {uploadState.loading && uploadPreview?.isStaged ? (
                          <div className="tablee-admin__document-save-overlay" role="status" aria-live="polite">
                            <span className="tablee-admin__document-spinner" aria-hidden="true" />
                            <strong>Guardando documento...</strong>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <small className="tablee-admin__document-file-name">
                      {uploadPreview ? uploadPreview.fileName : "PDF, JPG o PNG"}
                    </small>
                    <button
                      type="button"
                      className="tablee-admin__document-save"
                      onClick={() => saveDocument(activeModal.order)}
                      disabled={!uploadPreview?.isStaged || uploadState.loading}
                    >
                      {uploadState.loading ? "Guardando..." : "Guardar"}
                    </button>
                    {uploadState.message ? (
                      <p className="tablee-admin__upload-message">{uploadState.message}</p>
                    ) : null}
                  </section>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        confirmLabel={confirmDialog?.confirmLabel}
        tone={confirmDialog?.tone}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    </section>
  );
}

export default TableeEcommerceOrders;
